'use client'
import { ApplyToTrialButton } from '@components/app/Button/ApplyToTrial/index.client'
import { ShareTrialButton } from '@components/app/Button/ShareTrialButton/index.client'
import { useTrialAttributes } from '@components/app/TrialSummaryCard/AttributesString'
import TrialSiteImage from '@components/app/TrialSummaryCard/TrialSiteImage.client'
import { Icon } from '@components/primitives/Icon'
import { Text } from '@components/primitives/Text'
import { classMerge } from '@components/utilities/classMerge'
import type { IconDefinition } from '@fortawesome/fontawesome-common-types'
import { faBadgeCheck } from '@fortawesome/pro-solid-svg-icons/faBadgeCheck'
import { faBarsProgress } from '@fortawesome/pro-solid-svg-icons/faBarsProgress'
import { faHospitalUser } from '@fortawesome/pro-solid-svg-icons/faHospitalUser'
import { faIdCardAlt } from '@fortawesome/pro-solid-svg-icons/faIdCardAlt'
import { faLocationDot } from '@fortawesome/pro-solid-svg-icons/faLocationDot'
import { faUserMd } from '@fortawesome/pro-solid-svg-icons/faUserMd'
import type { BackgroundColor, TextColor } from '@lib/colors/colors'
import { TrackingSources } from '@lib/tracking/TrackingSources'
import { getPhaseFromPhaseList } from '@modules/trials/helpers/getPhaseFromPhaseList'
import getPreferredTrialTitle from '@modules/trials/helpers/getPreferredTrialTitle'
import type { TrialLocationNarrow } from '@modules/trials/types/TrialAssociatedWithLocations'
import type { TrialAssociation } from '@modules/trials/types/TrialAssociation'
import type { UtmLookup } from '@prisma/client'
import { useEffect, useState } from 'react'
import { TrialImagesCarousel } from './RightSideHeadingCards'
import { TreatmentClass } from './TrialHighlights'

export const HeroWithoutGradient = ({
  heroRef,
  isMonetizedTrial = false,
  nearestTrialLocations,
  shareTrialCodes,
  trial,
}: {
  heroRef?: (node?: Element | null) => void
  isMonetizedTrial?: boolean
  nearestTrialLocations?: TrialLocationNarrow[]
  shareTrialCodes?: UtmLookup[]
  trial: TrialAssociation
}) => {
  const trialTitle = getPreferredTrialTitle(trial)
  return (
    <div className='flex w-full flex-col gap-4 md:mt-4'>
      <div className='flex flex-col items-center justify-between gap-4 md:flex-row'>
        <div className='flex w-full flex-col gap-4 md:max-w-[800px]'>
          <div className='flex flex-row items-center gap-4' ref={heroRef}>
            {nearestTrialLocations ? (
              <TrialSiteImage
                className='rounded-xl'
                containerClass='size-[90px] min-w-[90px] hidden md:inline-block'
                trialDigitOffset={0}
                trialLocations={nearestTrialLocations}
              />
            ) : (
              <div className='hidden size-[90px] min-w-[90px] animate-pulse rounded-xl bg-neutral100 md:inline-block' />
            )}
            <div>
              <TreatmentClass color='text-neutral600' trial={trial} />
              <Text
                className='text-2xl font-extrabold text-black md:text-3xl md:leading-[115%]'
                element='h1'
                suppressHydrationWarning
              >
                {trialTitle}
                {trial.acronym && (
                  <>
                    {' '}
                    <Text
                      className='text-xl font-semibold md:text-xl md:leading-[180%]'
                      element='span'
                    >
                      {`(${trial.acronym} Trial)`}
                    </Text>
                  </>
                )}
              </Text>
            </div>
          </div>
          <div className='flex flex-col gap-4 md:hidden'>
            <div className={'w-full'}>
              <ApplyToTrialButton
                buttonText='Apply today'
                className='inline-block h-16'
                color='primary'
                source={TrackingSources.LISTING_HERO}
                trial={trial}
              />
            </div>
            <div className='w-full'>
              {shareTrialCodes && (
                <ShareTrialButton
                  color={'white'}
                  shareTrialCodes={shareTrialCodes}
                  trialSlug={trial.slug!}
                  trialTitle={trialTitle}
                />
              )}
            </div>
          </div>
          <div className='flex flex-wrap items-center gap-2 md:mt-4'>
            <TrialStatsPills
              isMonetizedTrial={isMonetizedTrial}
              trial={trial}
            />
          </div>
        </div>
        <div className='hidden w-1/2 md:visible md:flex md:flex-row md:gap-4'>
          <div className='w-full md:w-1/3'>
            {shareTrialCodes && (
              <ShareTrialButton
                color={'white'}
                shareTrialCodes={shareTrialCodes}
                trialSlug={trial.slug!}
                trialTitle={trialTitle}
              />
            )}
          </div>
          <div className={classMerge('w-full md:w-2/3')}>
            <ApplyToTrialButton
              buttonText='Apply today'
              className='h-12 py-2'
              color='primary'
              source={TrackingSources.LISTING_HERO}
              trial={trial}
            />
          </div>
        </div>
      </div>
      <TrialImagesCarousel trial={trial} />
    </div>
  )
}

const TrialStatsPills = ({
  isMonetizedTrial,
  trial,
}: {
  isMonetizedTrial?: boolean
  trial: TrialAssociation
}) => {
  const [nearestLocation, setNearestLocation] = useState<string>()
  const trialAttributes = useTrialAttributes(trial.trialLocations)

  useEffect(() => {
    const { Distance, NearestCity } = trialAttributes
    setNearestLocation(`${NearestCity} ${Distance ? Distance : ''}`)
  }, [trialAttributes])

  const principalInvestigator = trial.overallOfficials.find(
    (sponsor) => sponsor.role === 'Principal Investigator',
  )
  const leadSponsor = trial.overallOfficials.find(
    (sponsor) => sponsor.role === 'Lead Sponsor',
  )

  return (
    <>
      {isMonetizedTrial && (
        <TrialHeroPill
          bgColor='bg-primary50'
          icon={faBadgeCheck}
          iconColor='text-primary500'
          text={`Verified Trial`}
        />
      )}
      {nearestLocation && (
        <TrialHeroPill icon={faLocationDot} text={`${nearestLocation}`} />
      )}

      <TrialHeroPill
        icon={faBarsProgress}
        text={getPhaseFromPhaseList(trial.phaseList)}
      />

      <TrialHeroPill
        icon={faHospitalUser}
        text={
          trial.overallStatus === 'Recruiting'
            ? 'Recruiting'
            : 'Waitlist Available'
        }
      />

      {principalInvestigator?.name && (
        <TrialHeroPill
          icon={faUserMd}
          text={`Led By ${principalInvestigator.name}`}
        />
      )}

      {leadSponsor?.name && (
        <TrialHeroPill
          icon={faIdCardAlt}
          text={`Research Sponsored by ${leadSponsor.name}`}
        />
      )}
    </>
  )
}

const TrialHeroPill = ({
  bgColor = 'bg-neutral50',
  icon,
  iconColor = 'text-neutral800',
  text,
}: {
  bgColor?: BackgroundColor
  icon: IconDefinition
  iconColor?: TextColor
  text: string
}) => (
  <div
    className={classMerge(
      'flex items-center rounded-md border border-neutral200 px-2 py-1',
      bgColor,
    )}
  >
    <Icon className={classMerge('mr-2', iconColor)} icon={icon} />
    <Text
      className={`line-clamp-1 ${iconColor}`}
      styleName='p-tiny'
      value={text}
    />
  </div>
)
